import { useContext, useEffect, useState } from "react";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4, { toCamelCaseFromPascal } from "./Step4";
import { Service } from "../../../models/service.model";
import { AppDispatch, RootState } from "../../../redux/store";
import { useDispatch } from "react-redux";
import { formatCurrency } from "../../../utils";
import { useSelector } from "react-redux";
import { Subscription } from "../../../models/annual-plan.model";
import { getOfficeAnnualPlans, updateOfficeAnnualPlanServices, updateOfficePlan } from "../../../redux/slices/annualPlanSlice";
import { ToastContext } from "../../../contexts/ToastContext/ToastContext";
import { ToastVariants } from "../../../models";
import { getOffice } from "../../../redux/slices/OfficeSlice";
import { stripHtml } from "../../../utils/stripHtml";
import { PLAN_FORM_DATA, PlanFormDataType } from "../../../utils/planConstant";

let initialized = false;

const AnnualPlan = ({ officeId, handleClose }: { officeId: string; handleClose: () => void }) => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<PlanFormDataType[]>(PLAN_FORM_DATA);
  const [patientNotifications, setPatientNotifications] = useState("");
  const [services, setServices] = useState<Service[]>([]);
  // const selectedPlans = formData.reduce((acc: any, curr) => {
  //   acc[curr.id] = curr.value;
  //   return acc;
  // }, {});

  const selectedAnualPlans = useSelector((state: RootState) => state.selectedPlans.selectedPlans);
  const validPlanNames = selectedAnualPlans.map(plan => plan.planName);
  const selectedPlans = formData.reduce((acc: any, curr) => {
    // If the object has a planName and it's not in the valid list, set value to empty
    const shouldInvalidate = curr.planName && !validPlanNames.includes(curr.planName);

    acc[curr.id] = shouldInvalidate ? "" : curr.value;
    return acc;
  }, {});
  const { currentOffice } = useSelector((state: RootState) => state.office);
  const { subscriptionsMonthly, subscriptionsYearly, annualPlans } = useSelector((state: RootState) => state.annualPlan);
  const monthlyOptions = subscriptionsMonthly.map((item: Subscription) => ({
    value: item.subscriptionId,
    label: formatCurrency((item.price / 100).toString(), "blur"),
  }));
  const yearlyOptions = subscriptionsYearly.map((item: Subscription) => ({
    value: item.subscriptionId,
    label: formatCurrency((item.price / 100).toString(), "blur"),
  }));

  const changeStep = async (isCancel?: boolean) => {
    if (isCancel) {
      if (currentStep === 1) {
        handleClose();
      } else {
        setCurrentStep(currentStep - 1);
      }
    } else {
      if (currentStep === 4) {
        setLoading(true);
        const notification = stripHtml(patientNotifications);
        const response = await dispatch(
          updateOfficePlan({
            officeId: officeId,
            body: {
              annualPlanActive: true,
              annualPlanDescription: notification,
              ...selectedPlans,
            },
          })
        );

        if (response.payload) {
          const body = services
            .filter(item => item?.annualPlanDiscountPct && item?.annualPlanDiscountPct > 0)
            .map(item => ({
              serviceId: item.serviceId ?? "",
              annualPlanEnabled: item.annualPlanEnabled ?? false,
              annualPlanDiscountPct: item.annualPlanDiscountPct ?? 0,
            }));
          await dispatch(
            updateOfficeAnnualPlanServices({
              officeId: officeId,
              body: body,
            })
          );
          await dispatch(
            getOffice({
              officeId: officeId,
            })
          );
          await dispatch(
            getOfficeAnnualPlans({
              officeId: officeId,
            })
          );
          toast?.openToast("Plan activated successfully!", 2000, ToastVariants.Success);
        } else
          toast?.openToast(
            "There was an error activating your annual plan. Please check your network connection and try again.",
            2000,
            ToastVariants.Error
          );
        setLoading(false);
        handleClose();
      }
      setCurrentStep(currentStep + 1);
    }
  };

  useEffect(() => {
    if (!initialized && annualPlans?.length) {
      const selectedPlans = annualPlans.map(item => {
        return {
          id: `${toCamelCaseFromPascal(item.annualPlanType.name.split(" ").join(""))}Plan${
            item.subscription.subscriptionType === "monthly" ? "Monthly" : "Yearly"
          }SubscriptionId`,
          subscriptionId: item.subscription.subscriptionId,
        };
      });
      const updatedFormData = formData.map(item => {
        const selectedPlan = selectedPlans.find(plan => plan.id === item.id);
        if (selectedPlan) {
          return { ...item, value: selectedPlan.subscriptionId, valid: true };
        }
        return item;
      });
      setFormData(updatedFormData);
      initialized = true;
    }
    return () => {
      initialized = false;
    };
  }, [annualPlans]);

  useEffect(() => {
    if (currentOffice && currentOffice?.annualPlanDescription) {
      setPatientNotifications(currentOffice?.annualPlanDescription);
    }
  }, [currentOffice?.annualPlanDescription]);

  return (
    <div className="relative px-5">
      <div className="flex w-full overflow-hidden">
        <div className={`w-1/4 relative text-center overflow-hidden pb-12`}>
          <span className="status-number" />
          <span className="status-title">Rate</span>
        </div>
        <div className={`w-1/4 relative text-center overflow-hidden pb-12 ${currentStep < 2 ? "incomplete-step" : ""}`}>
          <span className="status-number" />
          <span className="status-title">Plan Services</span>
        </div>
        <div className={`w-1/4 relative text-center pb-12 ${currentStep < 3 ? "incomplete-step" : ""}`}>
          <span className="status-number" />
          <span className="status-title top-[33px]">Annual Plan Description</span>
        </div>
        <div className={`w-1/4 relative text-center overflow-hidden pb-12 ${currentStep < 4 ? "incomplete-step" : ""}`}>
          <span className="status-number" />
          <span className="status-title">Summary</span>
        </div>
      </div>
      {currentStep === 1 ? (
        <Step1 formData={formData} monthlyOptions={monthlyOptions} yearlyOptions={yearlyOptions} setFormData={setFormData} changeStep={changeStep} />
      ) : null}
      {currentStep === 2 ? <Step2 officeId={officeId} services={services} setServices={setServices} changeStep={changeStep} /> : null}
      {currentStep === 3 ? <Step3 value={patientNotifications} onChange={setPatientNotifications} changeStep={changeStep} /> : null}
      {currentStep === 4 ? (
        <Step4
          loading={loading}
          selectedPlans={selectedPlans}
          monthlyOptions={monthlyOptions}
          yearlyOptions={yearlyOptions}
          services={services}
          patientNotifications={patientNotifications}
          changeStep={changeStep}
        />
      ) : null}
    </div>
  );
};

export default AnnualPlan;
